import type { MessageFooterHandler } from './types';

const FOOTERS: MessageFooterHandler[] = [];

/**
 * Register a message footer handler.
 * Footer handlers are evaluated on every render to determine if they should be shown.
 *
 * @param handler - The footer handler configuration
 *
 * @example
 * ```ts
 * registerMessageFooter({
 *   id: 'tool-approval',
 *   priority: 10,
 *   showLatestOnly: true,
 *   shouldShow: (messages) => hasPendingApproval(messages),
 *   render: ({ messages }) => <WaitingForApproval count={getCount(messages)} />,
 * });
 * ```
 */
export function registerMessageFooter(handler: MessageFooterHandler): void {
  // Remove any existing handler with the same id
  const existingIndex = FOOTERS.findIndex((f) => f.id === handler.id);
  if (existingIndex !== -1) {
    FOOTERS.splice(existingIndex, 1);
  }
  FOOTERS.push(handler);
}

/**
 * Get all registered footer handlers, sorted by priority (highest first).
 * If handlers have the same priority, they're sorted by registration order.
 */
export function getMessageFooters(): MessageFooterHandler[] {
  return [...FOOTERS].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
}

/**
 * Unregister a footer handler by id.
 * @param id - The id of the footer handler to remove
 */
export function unregisterMessageFooter(id: string): void {
  const index = FOOTERS.findIndex((f) => f.id === id);
  if (index !== -1) {
    FOOTERS.splice(index, 1);
  }
}
